<template>
  <Dialog
    :visible="visible"
    modal
    :closable="!submitting"
    :show-header="false"
    :style="{ width: '560px', maxWidth: '95vw' }"
    :content-style="{ padding: '0', borderRadius: '16px' }"
    :pt="{ root: { class: 'add-payment-root' } }"
    @update:visible="onVisibleChange"
  >
    <!-- Custom header -->
    <header class="add-payment__header">
      <div class="add-payment__header-icon">
        <i class="fa-regular fa-credit-card" />
      </div>
      <div class="add-payment__header-text">
        <h2 class="add-payment__title">
          {{ t('billing.addCard.title') }}
        </h2>
        <p class="add-payment__subtitle">
          {{ t('billing.addCard.subtitle') }}
        </p>
      </div>
      <button
        type="button"
        class="add-payment__close"
        :disabled="submitting"
        :aria-label="t('billing.addCard.close')"
        @click="onClose"
      >
        <i class="fa-regular fa-xmark" />
      </button>
    </header>

    <div class="add-payment__body">
      <!-- Drop-in : unmounted on error (remounts fresh via :key bump
           when the user clicks « Réessayer ») and on success. -->
      <div
        v-if="!errorMessage && !successMessage"
        class="add-payment__dropin-wrap"
      >
        <AdyenCardDropin
          v-if="visible"
          :key="dropinKey"
          :amount="0"
          :currency="currency"
          :allowed-methods="ALLOWED_METHODS_ONESHOT"
          :submit-handler="onDropinSubmit"
          :details-handler="onDropinDetails"
          @success="onDropinSuccess"
          @error="onDropinError"
        />
      </div>

      <!-- Error state with retry CTA -->
      <div
        v-if="errorMessage"
        class="add-payment__error"
      >
        <div class="add-payment__error-icon">
          <i class="fa-regular fa-triangle-exclamation" />
        </div>
        <div class="add-payment__error-body">
          <div class="add-payment__error-title">
            {{ t('billing.addCard.errorTitle') }}
          </div>
          <div class="add-payment__error-message">
            {{ errorMessage }}
          </div>
        </div>
        <button
          type="button"
          class="add-payment__retry-btn"
          @click="onRetry"
        >
          <i class="fa-regular fa-rotate-left" />
          <span>{{ t('billing.addCard.retry') }}</span>
        </button>
      </div>

      <!-- Success state -->
      <div
        v-if="successMessage"
        class="add-payment__feedback add-payment__feedback--success"
      >
        <div class="add-payment__feedback-icon">
          <i class="fa-regular fa-check" />
        </div>
        <div class="add-payment__feedback-text">
          {{ successMessage }}
        </div>
      </div>

      <!-- Security footer (hidden in success state — already obvious) -->
      <div
        v-if="!successMessage && !errorMessage"
        class="add-payment__security"
      >
        <i class="fa-regular fa-shield-check" />
        <span>{{ t('billing.addCard.security') }}</span>
      </div>
    </div>

    <footer class="add-payment__foot">
      <button
        type="button"
        class="add-payment__btn-close"
        :disabled="submitting"
        @click="onClose"
      >
        {{ t('billing.addCard.close') }}
      </button>
    </footer>
  </Dialog>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Dialog from 'primevue/dialog';
import AdyenCardDropin from '@/components/billing/dialogs/AdyenCardDropin.vue';
import {
  ActionResult,
  addPaymentMethodApi,
  ALLOWED_METHODS_ONESHOT,
  confirmAddPaymentMethodApi,
} from '@/composables/billing/billingApi';

export default defineComponent({
  name: 'AddPaymentMethodDialog',

  components: {
    Dialog,
    AdyenCardDropin,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'added'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const submitting = ref(false);
    const errorMessage = ref<string | null>(null);
    const successMessage = ref<string | null>(null);
    // Currency derives from the overview. Shopify shops are rejected backend-
    // side when calling `addPaymentMethodApi`, but we keep the front aligned
    // with the rule (no hardcoded EUR on a Shopify-themed flow).
    const currency = computed<'EUR' | 'USD'>(
      () => (store.state.billing.overview?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    // Forces a fresh remount of <AdyenCardDropin> on retry: Adyen's drop-in
    // does not recover cleanly from a failed 3DS / declined challenge once it
    // has transitioned out of the 'ready' state, so the simplest and most
    // predictable fix is to destroy + recreate it.
    const dropinKey = ref(0);

    const onClose = () => {
      if (submitting.value) return;
      errorMessage.value = null;
      successMessage.value = null;
      emit('update:visible', false);
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onRetry = () => {
      errorMessage.value = null;
      dropinKey.value += 1;
    };

    const onDropinSubmit = (
      stateData: Record<string, any>,
    ): Promise<ActionResult> => addPaymentMethodApi(stateData);

    const onDropinDetails = (
      stateData: Record<string, any>,
    ): Promise<ActionResult> => confirmAddPaymentMethodApi(stateData);

    const onDropinSuccess = async () => {
      successMessage.value = t('billing.addCard.success');
      errorMessage.value = null;
      await store.dispatch('billing/refetchOverview');
      emit('added');
      // Auto-close after a short delay so the user sees the success state.
      setTimeout(() => {
        emit('update:visible', false);
      }, 1600);
    };

    const onDropinError = (err: string) => {
      errorMessage.value = err;
      successMessage.value = null;
    };

    return {
      t,
      ALLOWED_METHODS_ONESHOT,
      submitting,
      errorMessage,
      successMessage,
      dropinKey,
      currency,
      onVisibleChange,
      onClose,
      onRetry,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.add-payment {
  &__header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 24px 28px 20px;
    border-bottom: 1px solid #f1f5f9;
  }

  &__header-icon {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(148, 200, 64, 0.16) 0%, rgba(148, 200, 64, 0.04) 100%);
    color: $brand-color-primary;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
  }

  &__header-text {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.01em;
    line-height: 1.25;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #64748b;
    margin: 4px 0 0;
    line-height: 1.5;
  }

  &__close {
    border: none;
    background: transparent;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    cursor: pointer;
    transition: all 160ms ease;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: #f1f5f9;
      color: #0f172a;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    i {
      font-size: 1rem;
    }
  }

  &__body {
    padding: 22px 28px 8px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__dropin-wrap {
    // Adyen Drop-in injects its own card UI. We just give it a clean
    // background and a tiny lift so it doesn't sit flush against the modal.
    background: #fafbfc;
    border: 1px solid #eef0f3;
    border-radius: 12px;
    padding: 16px;
  }

  &__error {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    border-radius: 12px;
    background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
    border: 1px solid #fecaca;
    animation: add-payment-fade-in 240ms ease-out;
  }

  &__error-icon {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fee2e2;
    color: #dc2626;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.0625rem;
  }

  &__error-body {
    flex: 1;
    min-width: 0;
  }

  &__error-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #991b1b;
    line-height: 1.3;
  }

  &__error-message {
    font-size: 0.8125rem;
    color: #b91c1c;
    line-height: 1.45;
    margin-top: 2px;
    overflow-wrap: anywhere;
  }

  &__retry-btn {
    flex-shrink: 0;
    background: #fff;
    border: 1px solid #fecaca;
    color: #b91c1c;
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 160ms ease;

    &:hover {
      background: #fef2f2;
      border-color: #fca5a5;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }

    i {
      font-size: 0.875rem;
    }
  }

  &__feedback {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 22px 20px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(148, 200, 64, 0.10) 0%, rgba(148, 200, 64, 0.02) 100%);
    border: 1px solid rgba(148, 200, 64, 0.28);
  }

  &__feedback-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $brand-color-primary;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9375rem;
    box-shadow: 0 4px 12px -3px rgba(148, 200, 64, 0.5);
    animation: add-payment-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  &__feedback-text {
    font-weight: 600;
    font-size: 0.9375rem;
    color: #2d5016;
  }

  &__security {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #94a3b8;
    font-size: 0.75rem;
    padding: 4px 0 0;

    i {
      font-size: 0.875rem;
      color: #cbd5e1;
    }
  }

  &__foot {
    padding: 16px 28px 20px;
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid #f1f5f9;
    margin-top: 8px;
  }

  &__btn-close {
    background: transparent;
    border: 1px solid #e2e8f0;
    color: #475569;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 160ms ease;

    &:hover:not(:disabled) {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #0f172a;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

@keyframes add-payment-pop {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes add-payment-fade-in {
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

<style lang="scss">
// Unscoped : Dialog root receives the class via :pt
.add-payment-root.p-dialog {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px -24px rgba(15, 23, 42, 0.32);
  max-height: 92vh;
  display: flex;
  flex-direction: column;

  .p-dialog-content {
    padding: 0 !important;
    border-radius: 0;
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
  }
}
</style>
